import { call, put } from 'redux-saga/effects';
import { getInvoices, getInvoice, createInvoice, updateInvoice, updateInvoiceLineItems, sendInvoice, cancelInvoice, updateInvoiceStatus, createCreditMemo, exportInvoicesIif, generateLastMonthInvoices, sendInvoiceToGateway, deleteInvoice, getInvoicesByBillingEntities, recordClientPayment, getClientsWithRecentInvoices, getInactiveClients, getInvoiceSummary, getClientBillingOverview, sendAllDraftInvoices, getInvoiceOrphans, addInvoiceOrphans, getPdfTemplateConfig, savePdfTemplateConfig, uploadPdfTemplate, downloadInvoicePdf, getIntuitConnectionStatus, getIntuitCustomers, getInvoicedPeriodForDate, getUnbilledReport, getBillingPreview, getInvoiceNumberPreview } from '../requests/invoices';
import { showAlert } from '../../slices/alerts';
import { safeErrorMessage } from '../../../utils/errorUtils';

export function* handleGetInvoices(action) {
    try {
        const { start, limit, status, callback, opts } = action.payload;
        const response = yield call(getInvoices, start, limit, status, opts);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (action.payload.callback) {
            action.payload.callback({ invoices: [], total: 0, start: 0, limit: 50 });
        }
    }
}

export function* handleGetInvoice(action) {
    try {
        const { id, callback } = action.payload;
        const response = yield call(getInvoice, id);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleCreateInvoice(action) {
    try {
        const { invoice, callback } = action.payload;
        const response = yield call(createInvoice, invoice);
        if (response.data?._id) {
            yield put(showAlert({ severity: 'success', message: 'Invoice created successfully' }));
        } else {
            yield put(showAlert({ severity: 'info', message: response.data?.message || 'No billable items found for this entity' }));
        }
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        const message = safeErrorMessage(error);
        yield put(showAlert({ severity: 'error', message }));
        if (action.payload.callback) {
            action.payload.callback(null, message);
        }
    }
}

export function* handleUpdateInvoice(action) {
    try {
        const { id, invoice, callback } = action.payload;
        const response = yield call(updateInvoice, id, invoice);
        yield put(showAlert({ severity: 'success', message: 'Invoice updated successfully' }));
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleUpdateInvoiceLineItems(action) {
    try {
        const { id, lineItems, callback } = action.payload;
        const response = yield call(updateInvoiceLineItems, id, lineItems);
        yield put(showAlert({ severity: 'success', message: 'Line items updated successfully' }));
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleSendInvoice(action) {
    try {
        const { id, callback } = action.payload;
        const response = yield call(sendInvoice, id);
        yield put(showAlert({ severity: 'success', message: 'Invoice sent successfully' }));
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleCancelInvoice(action) {
    try {
        const { id, callback } = action.payload;
        const response = yield call(cancelInvoice, id);
        yield put(showAlert({ severity: 'success', message: 'Invoice cancelled successfully' }));
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleUpdateInvoiceStatus(action) {
    try {
        const { id, status, callback } = action.payload;
        const response = yield call(updateInvoiceStatus, id, status);
        yield put(showAlert({ severity: 'success', message: 'Invoice status updated successfully' }));
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleCreateCreditMemo(action) {
    try {
        const { id, creditData, callback } = action.payload;
        const response = yield call(createCreditMemo, id, creditData);
        yield put(showAlert({ severity: 'success', message: 'Credit memo created successfully' }));
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleExportInvoicesIif(action) {
    const { opts, callback } = action.payload || {};
    try {
        const response = yield call(exportInvoicesIif, opts);
        yield put(showAlert({ severity: 'success', message: 'QuickBooks IIF file exported successfully' }));
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (callback) {
            callback(null);
        }
    }
}

export function* handleGenerateLastMonthInvoices(action) {
    try {
        const { month, dueDate, invoiceDate, callback } = action.payload;
        const response = yield call(generateLastMonthInvoices, month, dueDate, invoiceDate);
        yield put(showAlert({ severity: 'success', message: response.data.message || 'Invoices generated successfully' }));
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        const { callback } = action.payload;
        if (callback) {
            callback(null);
        }
    }
}

export function* handleSendInvoiceToGateway(action) {
    const { id, callback } = action.payload;
    try {
        const response = yield call(sendInvoiceToGateway, id);
        yield put(showAlert({ severity: 'success', message: response.data?.message || 'Invoice synced to payment gateway' }));
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (callback) {
            callback(null);
        }
    }
}

export function* handleDeleteInvoice(action) {
    try {
        const { id, callback } = action.payload;
        yield call(deleteInvoice, id);
        yield put(showAlert({ severity: 'success', message: 'Invoice deleted successfully' }));
        if (callback) {
            callback();
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleGetInvoicesByBillingEntities(action) {
    try {
        const { callback } = action.payload || {};
        const response = yield call(getInvoicesByBillingEntities);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (action.payload?.callback) {
            action.payload.callback([]);
        }
    }
}

export function* handleRecordClientPayment(action) {
    try {
        const { invoiceId, paymentData, callback } = action.payload;
        const response = yield call(recordClientPayment, invoiceId, paymentData);
        yield put(showAlert({ severity: 'success', message: 'Payment recorded successfully' }));
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleGetClientsWithRecentInvoices(action) {
    try {
        const { callback } = action.payload;
        const response = yield call(getClientsWithRecentInvoices);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
    }
}

export function* handleGetInactiveClients(action) {
    try {
        const { callback } = action.payload;
        const response = yield call(getInactiveClients);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (action.payload?.callback) {
            action.payload.callback([]);
        }
    }
}

export function* handleGetInvoiceSummary(action) {
    try {
        const { callback } = action.payload;
        const response = yield call(getInvoiceSummary);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (action.payload?.callback) {
            action.payload.callback(null);
        }
    }
}

export function* handleGetClientBillingOverview(action) {
    try {
        const { clientId, start, limit, callback } = action.payload;
        const response = yield call(getClientBillingOverview, clientId, start, limit);
        if (callback) {
            callback(response.data);
        }
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: error.response?.data?.message || 'Failed to load billing overview' }));
        if (action.payload?.callback) {
            action.payload.callback(null);
        }
    }
}

export function* handleSendAllDraftInvoices(action) {
    try {
        const { opts, callback } = action.payload || {};
        const response = yield call(sendAllDraftInvoices, opts);
        yield put(showAlert({ severity: 'success', message: response.data.message }));
        if (callback) callback(response.data);
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (action.payload?.callback) action.payload.callback(null);
    }
}

export function* handleGetInvoiceOrphans(action) {
    try {
        const { invoiceId, callback, opts } = action.payload;
        const response = yield call(getInvoiceOrphans, invoiceId, opts);
        if (callback) callback(response.data);
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (action.payload?.callback) action.payload.callback(null);
    }
}

export function* handleAddInvoiceOrphans(action) {
    try {
        const { invoiceId, body, callback } = action.payload;
        const response = yield call(addInvoiceOrphans, invoiceId, body);
        const message = body?.action === 'markPaid' ? 'Records marked as paid — they will not appear on an invoice' : 'Orphan records added to invoice';
        yield put(showAlert({ severity: 'success', message }));
        if (callback) callback(response.data);
    } catch (error) {
        const message = safeErrorMessage(error);
        yield put(showAlert({ severity: 'error', message }));
        if (action.payload?.callback) action.payload.callback(null, message);
    }
}

export function* handleGetPdfTemplateConfig(action) {
    try {
        const { callback } = action.payload;
        const response = yield call(getPdfTemplateConfig);
        if (callback) callback({ ok: true, data: response.data });
    } catch (error) {
        const status = error.response?.status;
        if (action.payload?.callback) action.payload.callback({ ok: false, status, message: safeErrorMessage(error) });
    }
}

export function* handleSavePdfTemplateConfig(action) {
    try {
        const { config, callback } = action.payload;
        const response = yield call(savePdfTemplateConfig, config);
        if (callback) callback({ ok: true, data: response.data });
    } catch (error) {
        const message = safeErrorMessage(error);
        yield put(showAlert({ severity: 'error', message }));
        if (action.payload?.callback) action.payload.callback({ ok: false, message });
    }
}

export function* handleUploadPdfTemplate(action) {
    try {
        const { formData, callback } = action.payload;
        const response = yield call(uploadPdfTemplate, formData);
        if (callback) callback({ ok: true, data: response.data });
    } catch (error) {
        const message = safeErrorMessage(error);
        yield put(showAlert({ severity: 'error', message }));
        if (action.payload?.callback) action.payload.callback({ ok: false, message });
    }
}

export function* handleDownloadInvoicePdf(action) {
    try {
        const { invoiceId, callback } = action.payload;
        const response = yield call(downloadInvoicePdf, invoiceId);
        if (callback) callback({ ok: true, data: response.data });
    } catch (error) {
        const message = safeErrorMessage(error);
        if (action.payload?.callback) action.payload.callback({ ok: false, message });
    }
}

export function* handleGetIntuitConnectionStatus(action) {
    const { callback } = action.payload || {};
    try {
        const response = yield call(getIntuitConnectionStatus);
        if (callback) callback(response.data);
    } catch (error) {
        if (callback) callback(null);
    }
}

export function* handleGetIntuitCustomers(action) {
    const { callback } = action.payload || {};
    try {
        const response = yield call(getIntuitCustomers);
        if (callback) callback(response.data);
    } catch (error) {
        if (callback) callback(null);
    }
}

export function* handleGetInvoicedPeriodForDate(action) {
    const { callback } = action.payload || {};
    try {
        const { date, projectId, accountId } = action.payload;
        const response = yield call(getInvoicedPeriodForDate, { date, projectId, accountId });
        if (callback) callback(response.data);
    } catch (error) {
        if (callback) callback(null);
    }
}

export function* handleGetInvoiceNumberPreview(action) {
    const { callback } = action.payload || {};
    try {
        const response = yield call(getInvoiceNumberPreview, action.payload.pattern);
        if (callback) callback(response.data);
    } catch (error) {
        if (callback) callback(null);
    }
}

export function* handleGetBillingPreview(action) {
    const { callback } = action.payload || {};
    try {
        const { clientId, month } = action.payload;
        const response = yield call(getBillingPreview, clientId, month);
        if (callback) callback(response.data);
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (callback) callback(null);
    }
}

export function* handleGetUnbilledReport(action) {
    const { callback } = action.payload || {};
    try {
        const { month } = action.payload;
        const response = yield call(getUnbilledReport, month);
        if (callback) callback(response.data);
    } catch (error) {
        yield put(showAlert({ severity: 'error', message: safeErrorMessage(error) }));
        if (callback) callback(null);
    }
}
